// ─────────────────────────────────────────────────────────────────────────────
// THE THREE BREAKPOINTS (UI-1; decisions 5, addendum 1).
//
//   phone    up to 767px      no sidebar, top bar + drawer
//   tablet   768px to 1023px  72px icon rail, sidebar opens as an overlay
//   desktop  1024px and up    full 272px sidebar, collapsible
//
// CSS cannot read variables inside media queries and this is Create React App, so
// stylesheets use the literal 767px / 768px and 1023px / 1024px, and JS uses these.
// The phone limit is 767px because the existing shell, the 16px input rule and most
// pages already switch at 768px. Any page a block touches that uses another width
// (640, 600, 900 ...) moves onto these three.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';

export const BREAKPOINTS = { phoneMax: 767, tabletMin: 768, tabletMax: 1023, desktopMin: 1024 };

export const MEDIA = {
  phone: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1023px)',
  desktop: '(min-width: 1024px)',
};

// 'phone' | 'tablet' | 'desktop' for a width in CSS pixels.
export function breakpointForWidth(width) {
  if (width <= BREAKPOINTS.phoneMax) return 'phone';
  if (width <= BREAKPOINTS.tabletMax) return 'tablet';
  return 'desktop';
}

// The current breakpoint from matchMedia. Server or test environments without
// matchMedia get 'desktop', which is also what the first server render assumes.
export function currentBreakpoint(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win || typeof win.matchMedia !== 'function') return 'desktop';
  if (win.matchMedia(MEDIA.phone).matches) return 'phone';
  if (win.matchMedia(MEDIA.tablet).matches) return 'tablet';
  return 'desktop';
}

// The one hook every component uses. It listens to the two media queries that bound
// the tablet range, so it re-renders only when a boundary is CROSSED, never on every
// resize event (no resize listener, no thrash).
export function useBreakpoint() {
  const [bp, setBp] = useState(() => currentBreakpoint());
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const lists = [window.matchMedia(MEDIA.phone), window.matchMedia(MEDIA.desktop)];
    const update = () => setBp(currentBreakpoint());
    for (const l of lists) {
      if (l.addEventListener) l.addEventListener('change', update);
      else if (l.addListener) l.addListener(update);   // Safari before 14
    }
    update();   // in case it changed between the first render and this effect
    return () => {
      for (const l of lists) {
        if (l.removeEventListener) l.removeEventListener('change', update);
        else if (l.removeListener) l.removeListener(update);
      }
    };
  }, []);
  return bp;
}
